import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Check, ArrowUpDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { PRICING_PAGE } from "@/constants/testIds";
import { Footer } from "@/components/Footer";

const INCLUDED_FEATURES = [
  "Biblioteca de Documentos con evidencia legal (LFT)",
  "Vacaciones, incidencias y salario con historial inmutable",
  "Asesor IA de RH para admin y empleados",
  "Recordatorios automáticos de firmas y políticas pendientes",
];

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 md:px-12 py-6">
        <Link to="/" className="flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal tracking-tight">CERH</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-charcoal/70 hover:text-charcoal transition-colors px-4 py-2">
            Iniciar sesión
          </Link>
          <Link to="/registro" className="text-sm font-semibold bg-petroleum text-white px-5 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
            Registrar mi empresa
          </Link>
        </div>
      </header>

      <section className="px-6 md:px-12 pt-8 pb-16 max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
            Un precio simple, según el tamaño de tu equipo.
          </h1>
          <p className="text-base md:text-lg text-charcoal/70 mt-4">
            Sin capa gratuita ni letras chiquitas. Tu banda se ajusta sola cuando crece o baja tu plantilla activa.
          </p>
        </div>

        <div className="flex items-center gap-3 mt-8">
          <span className={`text-sm font-medium ${!annual ? "text-charcoal" : "text-charcoal/50"}`}>Mensual</span>
          <Switch data-testid={PRICING_PAGE.cycleToggle} checked={annual} onCheckedChange={setAnnual} />
          <span className={`text-sm font-medium ${annual ? "text-charcoal" : "text-charcoal/50"}`}>
            Anual <span className="text-terracotta font-semibold">(10% de descuento)</span>
          </span>
        </div>

        <div className="flex items-start gap-3 bg-petroleum/[0.06] border border-petroleum/15 rounded-xl p-4 mt-6 max-w-2xl">
          <ArrowUpDown className="w-4 h-4 text-petroleum shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-sm text-charcoal/80">
            <span className="font-semibold text-charcoal">Tu banda se ajusta sola</span> según tus empleados activos:
            si <span className="font-semibold">subes</span> de banda, el cambio aplica de inmediato con prorrateo; si{" "}
            <span className="font-semibold">bajas</span>, aplica hasta tu próximo ciclo de facturación.
          </p>
        </div>

        <p className="text-sm text-charcoal/60 mt-6 max-w-2xl">
          Las 6 bandas incluyen <span className="font-semibold text-charcoal">exactamente las mismas funciones</span> —
          el precio solo cambia según tu número de empleados, nunca por funcionalidad.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mt-6">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={PRICING_PAGE.tierCard}
              className="bg-card border border-petroleum/10 rounded-2xl p-6 flex flex-col shadow-[0_8px_30px_rgba(14,79,79,0.06)] hover:-translate-y-1 hover:shadow-lg transition-all"
            >
              <p className="text-xs font-semibold text-terracotta uppercase tracking-wide min-h-[2rem] flex items-start">{p.label}</p>
              {p.contact_only ? (
                <>
                  <p className="font-heading text-2xl font-bold text-charcoal mt-3">Contáctanos</p>
                  <p className="text-xs text-charcoal/50 mt-1">Plan a medida para equipos grandes</p>
                  <a
                    data-testid={PRICING_PAGE.ctaButton}
                    href="mailto:contacto@cerh.com.mx?subject=Plan%2051%2B%20empleados"
                    className="mt-auto pt-6 text-center text-sm font-semibold bg-charcoal text-white px-4 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  >
                    Contáctanos
                  </a>
                </>
              ) : (
                <>
                  <p className="font-heading text-3xl font-bold text-charcoal mt-3">
                    ${((annual ? p.annual_monthly_equivalent : p.monthly_price) ?? 0).toLocaleString("es-MX")}
                    <span className="text-sm font-normal text-charcoal/50"> MXN/mes</span>
                  </p>
                  {annual && (
                    <p className="text-xs text-charcoal/50 mt-1">${p.annual_price.toLocaleString("es-MX")} MXN facturado una vez al año</p>
                  )}
                  <Link
                    data-testid={PRICING_PAGE.ctaButton}
                    to="/registro"
                    className="mt-auto pt-6 text-center text-sm font-semibold bg-petroleum text-white px-4 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  >
                    Registrar mi empresa
                  </Link>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 bg-card border border-petroleum/10 rounded-2xl p-6 max-w-2xl">
          <p className="font-heading font-semibold text-charcoal mb-3">Incluido en todas las bandas</p>
          <ul className="space-y-2">
            {INCLUDED_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-charcoal/70">
                <Check className="w-4 h-4 text-terracotta mt-0.5 shrink-0" strokeWidth={2} />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <Footer />
    </div>
  );
}
